import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CycleCountsList from './CycleCountsList';
import ViewCycleCount from './ViewCycleCount';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import '../Admin/Users.css';

const CycleCountsManager = () => {
  const { t } = useTranslation('warehouse');
  const [selectedCountId, setSelectedCountId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [currentStatus, setCurrentStatus] = useState(null);
  const listRef = useRef();
  const viewRef = useRef();

  // Workflow actions (start / complete / approve) live in ViewCycleCount, next to the items.
  const renderToolbar = () => {
    if (viewMode === 'list') {
      return (
        <button className="toolbar-btn" onClick={handleRefresh}>
          <span className="icon">↻</span> {t('common.buttons.refresh', 'Refresh')}
        </button>
      );
    }
    if (viewMode === 'create' || viewMode === 'edit') {
      return (
        <>
          <button className="toolbar-btn primary" onClick={handleSave}>
            <span className="icon">💾</span> {t('common.buttons.save', 'Save')}
          </button>
          <button className="toolbar-btn" onClick={handleCancel}>
            <span className="icon">✖</span> {t('common.buttons.cancel', 'Cancel')}
          </button>
        </>
      );
    }
    return (
      <>
        {currentStatus === 'SCHEDULED' && (
          <>
            <button className="toolbar-btn" onClick={handleEdit}>
              <span className="icon">✏️</span> {t('common.buttons.edit', 'Edit')}
            </button>
            <button className="toolbar-btn danger" onClick={handleDelete}>
              <span className="icon">🗑️</span> {t('common.buttons.delete', 'Delete')}
            </button>
          </>
        )}
        <button className="toolbar-btn" onClick={handleBack}>
          <span className="icon">⬅️</span> {t('common.buttons.back', 'Back')}
        </button>
      </>
    );
  };

  const handleSelectCount = (countId) => {
    setSelectedCountId(countId);
    setViewMode('view');
  };

  const handleBack = () => {
    setSelectedCountId(null);
    setViewMode('list');
    setCurrentStatus(null);
    if (listRef.current) {
      listRef.current.fetchCounts();
    }
  };

  const handleNew = () => {
    setSelectedCountId(null);
    setViewMode('create');
    setCurrentStatus(null);
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedCountId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleSave = () => {
    if (viewRef.current) {
      viewRef.current.handleSave();
    }
  };

  const handleDelete = () => {
    if (viewRef.current) {
      viewRef.current.handleDelete();
    }
  };

  const handleRefresh = () => {
    if (listRef.current) {
      listRef.current.fetchCounts();
    }
  };

  const handleCountCreated = (countId) => {
    setSelectedCountId(countId);
    setViewMode('view');
    if (listRef.current) {
      listRef.current.fetchCounts();
    }
  };

  const handleSaveComplete = () => {
    setViewMode('view');
    if (listRef.current) {
      listRef.current.fetchCounts();
    }
  };

  // Track the status of the currently viewed count for toolbar context
  const handleCountLoaded = (status) => {
    setCurrentStatus(status);
  };

  return (
    <div className="departments-page">
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', padding: '0 0 8px' }}>
        {renderToolbar()}
      </div>
      <ResizablePanels
        leftPanel={
          <CycleCountsList
            ref={listRef}
            onSelectCount={handleSelectCount}
            selectedCountId={selectedCountId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewCycleCount
              ref={viewRef}
              countId={selectedCountId}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onBack={handleBack}
              onEdit={handleEdit}
              onSave={handleSaveComplete}
              onCancel={handleCancel}
              onCountCreated={handleCountCreated}
              onStatusChange={handleCountLoaded}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
};

export default CycleCountsManager;
